// ui/src/components/finance/SettlementLedger.jsx
/**
 * @file SettlementLedger.jsx
 * @module ui/src/components/finance/SettlementLedger
 * @description
 *   Renders Finance settlement lineage using separate original-economic-period and
 *   settlement-posting-period dimensions. The ledger preserves the temporal distinction
 *   between when an economic event occurred and when its correction is recorded.
 *
 * @version 1.0.0
 * @since S756
 * @author data-fit LLC · DecisionFlow · S776
 *
 * @architecture
 *   Locked decisions : #25
 *   Temporal invariants : INV-1, INV-3, INV-9
 *   Design principles : DP-1, DP-5, DP-9, DP-14
 *   Cross-cutting contracts : XC-6, XC-7
 *
 * @domain-rules
 *   - Original economic period and settlement posting period are independent temporal
 *     dimensions and must always be rendered as separate columns.
 *   - Settlement lineage comes from the server; workflow status never establishes
 *     economic attribution.
 *   - A zero settlement is a valid calculated result and must never be hidden.
 *   - The settlement persistence model is pending m_tc_12, so endpoint unavailability
 *     is rendered as an explicit unavailable state rather than a generic failure.
 *
 * @session-6-migration-flags
 *   - settlement table — does not exist in DDL r57; pending m_tc_12
 *   - settlement.original_economic_period_id — pending m_tc_12
 *   - settlement.settlement_posting_period_id — pending m_tc_12
 *   - GET /api/v1/finance/settlements — Session 6 API gap
 *
 * @changelog
 *   1.0.0  S756  Initial build — Group Finance batch delivery
 */

import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * @typedef {Object} SettlementEntry
 * @property {string} entryId
 * @property {string} originalEventRef
 * @property {string} originalEconomicPeriodLabel
 * @property {number} originalEconomicPeriodId
 * @property {string} settlementPostingPeriodLabel
 * @property {number} settlementPostingPeriodId
 * @property {string} reason
 * @property {number} originalAmount
 * @property {number} correctionAmount
 * @property {number} netSettlementAmount
 * @property {string} governingPlanVersion
 * @property {string} postingRunId
 * @property {string} currency
 * @property {string} [postedAt]
 * @property {number} [planId]
 * @property {Object} [eventDetail]
 * @property {Object} [lineage]
 * @property {string} [auditReference]
 */

/**
 * @typedef {Object} SettlementLedgerProps
 * @property {number} fiscalPeriodId
 * @property {number|null} planId
 */

const SORT_OPTIONS = [
  ['posted_at_desc', 'Posted newest first'],
  ['posted_at_asc', 'Posted oldest first'],
  ['original_period_asc', 'Original economic period'],
  ['net_amount_desc', 'Net settlement highest first'],
];

const styles = {
  controls: {
    alignItems: 'end',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  label: {
    color: 'rgb(var(--color-text-muted))',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  input: {
    background: 'rgb(var(--color-surface))',
    border: '1px solid rgb(var(--color-border))',
    color: 'rgb(var(--color-text))',
    fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
    minHeight: '2.25rem',
    padding: '0.375rem 0.5rem',
  },
  numeric: {
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  expanded: {
    background: 'rgb(var(--color-surface-muted))',
    display: 'grid',
    gap: '0.75rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(13rem, 1fr))',
    padding: '0.75rem',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.2rem',
  },
  pre: {
    margin: 0,
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
  },
};

function formatMoney(value, currency) {
  // WHY: Exact numeric zero must be formatted as a genuine calculated result;
  // null/invalid values remain explicitly unavailable instead of coercing to zero.
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 'Not available';
  }

  try {
    return new Intl.NumberFormat(undefined, {
      currency,
      style: 'currency',
    }).format(value);
  } catch {
    // WHY: Invalid currency metadata must not suppress recorded settlement evidence.
    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(value);
  }
}

function SettlementLedger({ fiscalPeriodId, planId }) {
  // WHY: Sort is display/query preference only; it never changes settlement economics.
  const [sort, setSort] = useState('posted_at_desc');

  // WHY: Finance may narrow an all-plan period ledger without changing the immutable
  // original/posting period dimensions returned for each entry.
  const [filterPlanId, setFilterPlanId] = useState(planId);

  // WHY: One expanded entry at a time keeps lineage inspection attributable to a
  // specific settlement record.
  const [expandedEntryId, setExpandedEntryId] = useState(null);

  // WHY: Settlement evidence is server authoritative and stored verbatim for rendering.
  const [entries, setEntries] = useState([]);

  // WHY: Loading state prevents ambiguous empty-table presentation while evidence loads.
  const [loading, setLoading] = useState(false);

  // WHY: API/migration unavailability must be distinguished from a legitimate empty ledger.
  const [error, setError] = useState('');

  // WHY: m_tc_12 is not deployed in the confirmed baseline, so a 404 receives its own
  // explicit unavailable state instead of being reported as an ordinary system error.
  const [settlementUnavailable, setSettlementUnavailable] = useState(false);

  useEffect(() => {
    // WHY: A parent-supplied plan scope is authoritative when the viewed plan changes.
    setFilterPlanId(planId);
  }, [planId]);

  useEffect(() => {
    const controller = new AbortController();

    async function fetchSettlements() {
      // WHY: Changing posting period, plan scope, or server-supported sort requires a
      // fresh authoritative settlement result; local settlement inference is prohibited.
      setLoading(true);
      setError('');
      setSettlementUnavailable(false);
      setExpandedEntryId(null);

      const params = new URLSearchParams({
        fiscal_period_id: String(fiscalPeriodId),
        sort,
      });

      // WHY: plan_id is optional by contract; omitting it means all plans in the posting period.
      if (filterPlanId !== null && filterPlanId !== '') {
        params.set('plan_id', String(filterPlanId));
      }

      try {
        // WHY: Settlement lineage must come from the Finance settlement endpoint because
        // original and posting periods cannot be reconstructed safely in the browser.
        const response = await fetch(
          `/api/v1/finance/settlements?${params.toString()}`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
            signal: controller.signal,
          },
        );

        // WHY: 404 is expected while the m_tc_12 persistence/API gap remains open.
        if (response.status === 404) {
          setEntries([]);
          setSettlementUnavailable(true);
          return;
        }

        if (!response.ok) {
          throw new Error(`Settlement ledger request failed (${response.status}).`);
        }

        const payload = await response.json();
        setEntries(Array.isArray(payload.entries) ? payload.entries : []);
      } catch (requestError) {
        // WHY: Aborted requests are lifecycle cleanup, not user-visible ledger failures.
        if (requestError.name !== 'AbortError') {
          setEntries([]);
          setError(
            requestError.message || 'Settlement ledger could not be loaded.',
          );
        }
      } finally {
        // WHY: An aborted effect may already have been replaced; React safely ignores
        // obsolete state in cleanup while current requests leave loading explicitly.
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    fetchSettlements();

    // WHY: Prevent stale posting-period responses from replacing newer Finance context.
    return () => controller.abort();
  }, [fiscalPeriodId, filterPlanId, sort]);

  const sortedEntries = useMemo(() => {
    // WHY: Server remains authoritative, but deterministic local ordering protects the
    // requested presentation when an API implementation returns unsorted entries.
    const copy = [...entries];

    if (sort === 'original_period_asc') {
      return copy.sort(
        (a, b) =>
          a.originalEconomicPeriodId - b.originalEconomicPeriodId,
      );
    }

    if (sort === 'net_amount_desc') {
      return copy.sort(
        (a, b) => b.netSettlementAmount - a.netSettlementAmount,
      );
    }

    if (sort === 'posted_at_asc') {
      return copy.sort((a, b) =>
        String(a.postedAt || '').localeCompare(String(b.postedAt || '')),
      );
    }

    return copy.sort((a, b) =>
      String(b.postedAt || '').localeCompare(String(a.postedAt || '')),
    );
  }, [entries, sort]);

  const handleSortChange = (event) => {
    // WHY: Sort changes presentation only and triggers an authoritative refetch.
    setSort(event.target.value);
  };

  const handlePlanFilterChange = (event) => {
    // WHY: Blank means all plans; numeric conversion is limited to the query scope
    // and never changes entry lineage data.
    const nextValue = event.target.value.trim();
    setFilterPlanId(nextValue === '' ? null : Number(nextValue));
  };

  const handleToggleEntry = (entryId) => {
    // WHY: Expanded lineage belongs to one immutable settlement entry at a time.
    setExpandedEntryId((current) =>
      current === entryId ? null : entryId,
    );
  };

  return (
    <section
      aria-labelledby="settlement-ledger-title"
      className="modifier-manager-card"
      style={{ fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif' }}
    >
      <div className="modifier-manager-header">
        <div>
          <h2 id="settlement-ledger-title">Settlement ledger</h2>
          <p>
            Original economic timing and settlement posting timing are shown as
            separate dimensions for every entry.
          </p>
        </div>
      </div>

      <div className="modifier-manager-body">
        <div style={styles.controls}>
          <label style={styles.field}>
            <span style={styles.label}>Sort</span>
            <select
              aria-label="Sort settlement ledger"
              onChange={handleSortChange}
              style={styles.input}
              value={sort}
            >
              {SORT_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          {/*
           * WHY: When planId is not fixed by the parent, Finance can optionally scope the
           * endpoint by plan while preserving both temporal columns unchanged.
           */}
          {planId === null ? (
            <label style={styles.field}>
              <span style={styles.label}>Plan ID filter</span>
              <input
                inputMode="numeric"
                min="1"
                onChange={handlePlanFilterChange}
                placeholder="All plans"
                style={styles.input}
                type="number"
                value={filterPlanId ?? ''}
              />
            </label>
          ) : null}
        </div>

        {/*
         * WHY: Loading is distinct from an empty ledger and from m_tc_12 unavailability.
         */}
        {loading ? (
          <div aria-live="polite" role="status">
            <div className="skeleton">Loading settlement entries…</div>
          </div>
        ) : null}

        {/*
         * WHY: 404 means the pending settlement capability is unavailable, not that
         * Finance has zero valid settlements.
         */}
        {!loading && settlementUnavailable ? (
          <div
            className="modifier-manager-empty-state"
            role="status"
          >
            Settlement ledger is not available in this environment. The settlement
            persistence model and Finance endpoint are pending m_tc_12.
          </div>
        ) : null}

        {/*
         * WHY: Operational request failures must remain distinguishable from both
         * unavailable capability and a genuine zero-entry result.
         */}
        {!loading && error ? (
          <div
            className="modifier-manager-error-state"
            role="alert"
          >
            {error}
          </div>
        ) : null}

        {/*
         * WHY: An empty successful response is a legitimate Finance state and should
         * not be represented as an unavailable migration state.
         */}
        {!loading
        && !error
        && !settlementUnavailable
        && sortedEntries.length === 0 ? (
          <div
            className="modifier-manager-empty-state"
            role="status"
          >
            No settlement entries were recorded for this posting-period scope.
          </div>
        ) : null}

        {/*
         * WHY: Both period columns are structural invariants of the ledger and remain
         * present for every successful non-empty result.
         */}
        {!loading
        && !error
        && !settlementUnavailable
        && sortedEntries.length > 0 ? (
          <div className="modifier-manager-table-wrapper">
            <table className="modifier-manager-table">
              <thead>
                <tr>
                  <th scope="col">Original economic period</th>
                  <th scope="col">Settlement posting period</th>
                  <th scope="col">Original event ref</th>
                  <th scope="col">Reason</th>
                  <th scope="col">Original amount</th>
                  <th scope="col">Correction</th>
                  <th scope="col">Net settlement</th>
                  <th scope="col">Governing plan version</th>
                  <th scope="col">Posting run</th>
                </tr>
              </thead>

              <tbody>
                {sortedEntries.map((entry) => {
                  const expanded = expandedEntryId === entry.entryId;

                  return (
                    <React.Fragment key={entry.entryId}>
                      <tr>
                        <td>{entry.originalEconomicPeriodLabel}</td>
                        <td>{entry.settlementPostingPeriodLabel}</td>
                        <td>
                          <button
                            aria-expanded={expanded}
                            className="text-button"
                            onClick={() => handleToggleEntry(entry.entryId)}
                            type="button"
                          >
                            {entry.originalEventRef}
                          </button>
                        </td>
                        <td>{entry.reason}</td>
                        <td style={styles.numeric}>
                          {formatMoney(entry.originalAmount, entry.currency)}
                        </td>
                        <td style={styles.numeric}>
                          {formatMoney(entry.correctionAmount, entry.currency)}
                        </td>
                        <td style={styles.numeric}>
                          {formatMoney(entry.netSettlementAmount, entry.currency)}
                        </td>
                        <td>{entry.governingPlanVersion}</td>
                        <td>{entry.postingRunId}</td>
                      </tr>

                      {/*
                       * WHY: Expanded evidence exposes lineage associated with this exact
                       * settlement record and does not alter either temporal dimension.
                       */}
                      {expanded ? (
                        <tr>
                          <td colSpan={9}>
                            <div style={styles.expanded}>
                              <div style={styles.detailItem}>
                                <strong>Original economic period ID</strong>
                                <span>{entry.originalEconomicPeriodId}</span>
                              </div>

                              <div style={styles.detailItem}>
                                <strong>Settlement posting period ID</strong>
                                <span>{entry.settlementPostingPeriodId}</span>
                              </div>

                              <div style={styles.detailItem}>
                                <strong>Audit reference</strong>
                                <span>
                                  {entry.auditReference || 'Not available'}
                                </span>
                              </div>

                              <div style={styles.detailItem}>
                                <strong>Event detail</strong>
                                <pre style={styles.pre}>
                                  {entry.eventDetail
                                    ? JSON.stringify(entry.eventDetail, null, 2)
                                    : 'Not available'}
                                </pre>
                              </div>

                              <div style={styles.detailItem}>
                                <strong>Lineage</strong>
                                <pre style={styles.pre}>
                                  {entry.lineage
                                    ? JSON.stringify(entry.lineage, null, 2)
                                    : 'Not available'}
                                </pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </section>
  );
}

SettlementLedger.propTypes = {
  fiscalPeriodId: PropTypes.number.isRequired,
  planId: PropTypes.number,
};

SettlementLedger.defaultProps = {
  planId: null,
};

/**
 * @example
 * <SettlementLedger
 *   fiscalPeriodId={202608}
 *   planId={418}
 * />
 */

/**
 * DOMAIN COMPLIANCE CHECK
 *
 * ✓ C-33 / m_tc_12:
 *   "Original economic period" and "Settlement posting period" are separate,
 *   always-visible table columns with the locked labels.
 *
 * ✓ INV-1 / DP-9:
 *   Economic timing is rendered from server settlement lineage; no approval-date
 *   or proration logic exists.
 *
 * ✓ INV-3:
 *   Workflow statuses are not used for temporal attribution.
 *
 * ✓ XC-6:
 *   Numeric zero is formatted and displayed rather than suppressed.
 *
 * ✓ XC-7 / DP-14:
 *   Loading, unavailable, empty, error, and expanded states are stated in text.
 *
 * ✓ m_tc_12:
 *   HTTP 404 has a graceful explicit capability-unavailable state.
 *
 * ✓ DP-8:
 *   Prohibited Finance approval language is absent.
 */

/**
 * SESSION 6 MIGRATION FLAGS
 *
 * - settlement table — DOES NOT EXIST in DDL r57; pending m_tc_12.
 * - settlement.original_economic_period_id — pending m_tc_12.
 * - settlement.settlement_posting_period_id — pending m_tc_12.
 * - GET /api/v1/finance/settlements — Session 6 API gap.
 */

/**
 * ACCESSIBILITY NOTES
 *
 * - Both temporal dimensions use semantic column headers.
 * - Money is rendered as text and is not communicated by color.
 * - Expand controls expose aria-expanded.
 * - Loading, unavailable, empty, and error states have status/alert semantics.
 * - Native form controls remain keyboard accessible.
 */

export default SettlementLedger;
